import { Capacitor } from "@capacitor/core";
import { IOS_DOWNLOAD_LINK, ANDROID_DOWNLOAD_LINK } from "../api/constants";

const UNIVERSAL_LINK_ORIGIN = "https://app.inyc.us";
export const DEEPLINK_SENTINEL_PARAM = "inyc_app_intent";

/**
 * Simple, reliable app store opener
 * Detects platform and opens the appropriate store link
 * No deep link detection or complex fallbacks - just direct store links
 */
export const openAppStore = () => {
  let storeUrl;

  // Platform detection
  if (Capacitor.isNativePlatform()) {
    // Running in native app - use Capacitor platform detection
    storeUrl = Capacitor.getPlatform() === "ios" ? IOS_DOWNLOAD_LINK : ANDROID_DOWNLOAD_LINK;
  } else {
    // Running in web browser - detect mobile OS from user agent
    const userAgent = navigator.userAgent || navigator.vendor || window.opera;
    const isIOS = /iPad|iPhone|iPod/.test(userAgent) && !window.MSStream;
    storeUrl = isIOS ? IOS_DOWNLOAD_LINK : ANDROID_DOWNLOAD_LINK;
  }

  // Use window.location.href for reliable navigation
  window.location.href = storeUrl;

  return { success: true, url: storeUrl };
};

/**
 * Opens the inyc.us/download page for sharing purposes
 */
export const openDownloadPage = () => {
  const downloadUrl = "https://inyc.us/download";
  window.location.href = downloadUrl;
  return { success: true, url: downloadUrl };
};

/**
 * Build the universal link pointing to the same path/query/hash as the current page
 * and append a sentinel query param so the server can detect failed deep-link attempts.
 */
const buildUniversalLinkWithIntent = () => {
  if (typeof window === "undefined") {
    return UNIVERSAL_LINK_ORIGIN;
  }

  const currentUrl = new URL(window.location.href);

  // Remove any existing sentinel to avoid duplicates
  currentUrl.searchParams.delete(DEEPLINK_SENTINEL_PARAM);

  // Append sentinel flag so backend can detect a deeplink attempt
  currentUrl.searchParams.set(DEEPLINK_SENTINEL_PARAM, "1");

  const pathWithQuery = `${currentUrl.pathname}${currentUrl.search}${currentUrl.hash}`;
  return `${UNIVERSAL_LINK_ORIGIN}${pathWithQuery}`;
};

/**
 * Attempt to open the native app via the universal link while
 * marking the attempt with a sentinel query parameter.
 */
export const openAppViaUniversalLink = () => {
  const universalLink = buildUniversalLinkWithIntent();

  // Log user agent for debugging
  const userAgent = navigator.userAgent;
  const isIOS = /iPad|iPhone|iPod/.test(userAgent);
  const isAndroid = /Android/.test(userAgent);
  const isInstagram = /Instagram|FBAN|FBAV/.test(userAgent);

  // alert('[OpenApp] Full User Agent:' +  userAgent);
  // alert('[OpenApp] Platform Detection:' + JSON.stringify({ isIOS, isAndroid, isInstagram }));
  // alert('[OpenApp] Universal Link:' + universalLink);

  if (isIOS) {
    // iOS: Use redirect with blob URL (works in iOS Instagram)
    // const redirectHtml = `
    //   <!DOCTYPE html>
    //   <html>
    //     <head>
    //       <meta http-equiv="refresh" content="0;url=${universalLink}">
    //       <script>
    //         setTimeout(() => { window.location.href = '${universalLink.replace('https://app.inyc.us', 'inyc:')}'; }, 500);
    //       </script>
    //     </head>
    //     <body></body>
    //   </html>
    // `;


    // const blob = new Blob([redirectHtml], { type: 'text/html' });
    // const url = URL.createObjectURL(blob);
    // window.open(url, '_blank');

    const redirectHtml = `https://inyc.us/redirect?target=${encodeURIComponent(universalLink)}`;
    window.open(redirectHtml, '_blank')
    return universalLink;
  }
  else if (isAndroid && isInstagram) {
    // Android Instagram: Use intent URL with app package and fallback
    try {
      // Build intent URL with full app info and fallback
      const intentUrl = `intent:${universalLink}#Intent;` +
        `S.browser_fallback_url=${encodeURIComponent(universalLink)};` +
        `end`;
      
      console.log('[OpenApp] Android Instagram - Using intent with fallback:', intentUrl);
      window.location.href = intentUrl;
    } catch (error) {
      console.error('[OpenApp] Intent URL failed:', error);
      // Fallback
      window.location.href = universalLink;
    }

    return universalLink;
  }
  else if (isAndroid) {
    // Regular Android browser: Use App Links via window.open
    console.log('[OpenApp] Android browser detected - Using App Links');
    window.open(universalLink, '_blank', 'noopener,noreferrer');
    return universalLink;
  }

  // Fallback
  window.location.href = universalLink;
  return universalLink;

};

/**
 * Remove the deeplink intent sentinel parameter from the current URL (if present)
 * without reloading the page. Useful inside the native app to keep clean URLs.
 */
export const stripDeeplinkIntentParam = () => {
  if (typeof window === "undefined") {
    return false;
  }

  try {
    const currentUrl = new URL(window.location.href);
    if (!currentUrl.searchParams.has(DEEPLINK_SENTINEL_PARAM)) {
      return false;
    }

    currentUrl.searchParams.delete(DEEPLINK_SENTINEL_PARAM);

    const cleanPath = `${currentUrl.pathname}${currentUrl.search}${currentUrl.hash}`;
    window.history.replaceState({}, document.title, cleanPath);
    return true;
  } catch (error) {
    console.warn('[OpenApp] Failed to strip deeplink intent param', error);
    return false;
  }
};

/**
 * Smart "Open App" function - tries to open native app, falls back to store
 * Works like Instagram's "Open in App" button
 * 
 * Strategy:
 * 1. Try to open app via custom scheme (inyc://)
 * 2. Detect if app opened (page blur/visibility change)
 * 3. If app doesn't open within 2.5 seconds, redirect to store
 * 
 * Safari: Uses hybrid iframe + direct navigation (shows error but works)
 * Chrome: Direct navigation only
 */
export const openApp = () => {
  const userAgent = navigator.userAgent || navigator.vendor || window.opera;
  const isIOS = /iPad|iPhone|iPod/.test(userAgent) && !window.MSStream;
  const isAndroid = /android/i.test(userAgent);
  const isSafari = /Safari/.test(userAgent) && !/Chrome|CriOS|FxiOS/.test(userAgent);

  // If not mobile, just go to store
  if (!isIOS && !isAndroid) {
    openAppStore();
    return;
  }

  // Track if app opened
  let appOpened = false;
  let fallbackTimer = null;
  let listenersActive = false;

  // Mark app as opened when page loses focus
  const markAsOpened = () => {
    if (!appOpened && listenersActive) {
      appOpened = true;
      if (fallbackTimer) {
        clearTimeout(fallbackTimer);
      }
    }
  };

  // Listen for page becoming hidden (app opened)
  const handleVisibilityChange = () => {
    if (document.hidden) {
      markAsOpened();
    }
  };

  const handleBlur = () => {
    if (listenersActive) {
      markAsOpened();
    }
  };

  const handlePageHide = () => {
    markAsOpened();
  };

  // Cleanup function
  const cleanup = () => {
    listenersActive = false;
    document.removeEventListener('visibilitychange', handleVisibilityChange);
    window.removeEventListener('blur', handleBlur);
    window.removeEventListener('pagehide', handlePageHide);
  };

  // Try to open the app with custom scheme
  const deepLinkUrl = 'inyc://';

  // Safari: Hybrid approach (iframe + direct navigation)
  if (isSafari) {
    // Create hidden iframe
    const iframe = document.createElement('iframe');
    iframe.style.display = 'none';
    iframe.style.width = '0';
    iframe.style.height = '0';
    iframe.src = deepLinkUrl;
    document.body.appendChild(iframe);

    // Also try direct navigation after short delay
    setTimeout(() => {
      try {
        window.location.href = deepLinkUrl;
      } catch (e) {
        // Ignore errors
      }
    }, 25);

    // Clean up iframe
    setTimeout(() => {
      try {
        if (iframe.parentNode) {
          document.body.removeChild(iframe);
        }
      } catch (e) {
        // Ignore errors
      }
    }, 1000);
  } else {
    // Chrome/Other: Direct navigation
    try {
      window.location.href = deepLinkUrl;
    } catch (e) {
      // If immediate error, go to store
      cleanup();
      openAppStore();
      return;
    }
  }

  // Set up event listeners
  document.addEventListener('visibilitychange', handleVisibilityChange);
  window.addEventListener('blur', handleBlur);
  window.addEventListener('pagehide', handlePageHide);

  // Activate listeners after brief delay
  setTimeout(() => {
    listenersActive = true;
  }, 200);

  // Fallback timer - redirect to store if app doesn't open
  fallbackTimer = setTimeout(() => {
    cleanup();

    if (!appOpened) {
      openAppStore();
    }
  }, 2500);

  // Cleanup after 5 seconds (prevent memory leaks)
  setTimeout(() => {
    cleanup();
  }, 5000);
}; 